<template>
  <div ref="priceCard" class="book-now-container">
    <div class="price-display">
      <template v-if="hasPrice">
        <span class="price-label">From</span>
        <span class="price-value">
          ${{ displayPrice }}<span v-if="cabinLabel" class="price-cabin"> ({{ cabinLabel }})</span>
        </span>
        <span class="price-person">per person</span>
        <span class="price-freshness">{{ updatedLabel }}</span>
      </template>
      <template v-else>
        <span class="price-unavailable">{{ unavailableCopy }}</span>
        <span v-if="ageNote" class="price-freshness">{{ ageNote }}</span>
      </template>
    </div>

    <!-- CP-17: enabled state — affiliate partner link -->
    <a v-affiliate-link
      v-if="isAffiliateLink"
      class="cs-btn-book-primary book-now-link"
      :href="partnerUrl"
      target="_blank"
      rel="noopener noreferrer sponsored"
      :aria-label="`${tierCopy.cta} (opens booking partner in a new tab)`"
      aria-describedby="booking-affiliate-desc"
      @click="handleBookClick"
    >
      <i class="fas fa-external-link-alt" aria-hidden="true"></i>
      {{ tierCopy.cta }} ↗
    </a>

    <!-- CP-120: affiliate OFF — Price Alert owns the primary CTA slot under the
         price; the CP-97 quiet not-bookable status sits beneath it. -->
    <template v-else>
      <a
        v-if="alertTarget"
        class="cs-btn-book-primary book-now-link book-now-alert-cta"
        :href="`#${alertTarget}`"
        @click.prevent="goToAlert"
      >
        <i class="fas fa-bell" aria-hidden="true"></i>
        Create Price Alert
      </a>
      <p
        class="book-now-quiet book-now-inline"
        role="status"
        title="Direct booking is not available on this site"
      >
        Booking Not Available
      </p>
    </template>

    <div class="booking-info">
      <p v-if="disclosure.note">{{ disclosure.note }}</p>
      <p v-if="isAffiliateLink && tierCopy.landing" class="booking-tier-landing">{{ tierCopy.landing }}</p>
      <p v-if="isAffiliateLink" class="booking-tier-subline">{{ tierCopy.subLine }}</p>
      <div
        v-if="disclosure.partnerDisclosure"
        id="booking-affiliate-desc"
        class="cs-affiliate-disclosure"
      >
        <i class="fas fa-handshake" aria-hidden="true"></i>
        {{ disclosure.partnerDisclosure }}
        <router-link to="/disclosure">Learn more</router-link>
      </div>
      <p class="disclaimer">*Prices are subject to change. Additional fees may apply.</p>
    </div>
  </div>

  <!-- Sticky bottom Book Now for phone (CP-26 / CP-18c; CP-213 slim 56px bar).
       Appears only once the price card has scrolled out of view, and steps aside
       while the Price Alert form is on screen so it never covers the form.
       Fixed position: showing/hiding never shifts page layout. -->
  <div
    class="sticky-book-bar cs-safe-bottom"
    :class="{ 'sticky-book-bar--hidden': !stickyVisible }"
    :aria-hidden="stickyVisible ? 'false' : 'true'"
    data-testid="sticky-book-bar"
  >
    <div class="sticky-book-inner">
      <div class="sticky-price">
        <div class="sticky-price-row">
          <template v-if="hasPrice">
            <span class="sticky-from">From</span>
            <span class="sticky-amount">
              ${{ displayPrice }}<span v-if="cabinLabel" class="sticky-cabin"> ({{ cabinLabel }})</span>
            </span>
            <span class="sticky-per-person">/person</span>
          </template>
          <span v-else class="sticky-unavailable">{{ unavailableCopy }}</span>
        </div>
        <!-- CP-154 / CP-213: one-line disclosure, flag ON only (OFF output unchanged).
             The full disclosure stays in the price card; "Learn more" jumps to it. -->
        <p v-if="isAffiliateLink" class="sticky-disclosure" data-testid="sticky-disclosure">
          <span id="sticky-affiliate-desc">{{ stickyDisclosureText }}</span>
          <a
            class="sticky-disclosure-link"
            :href="`#${fullDisclosureId}`"
            data-testid="sticky-disclosure-link"
            @click.prevent="goToDisclosure"
          >{{ stickyDisclosureLinkText }}</a>
        </p>
        <!-- CP-120: affiliate OFF — quiet status below the price, not the CTA slot -->
        <p
          v-else
          class="book-now-quiet sticky-quiet"
          role="status"
          title="Direct booking is not available on this site"
        >
          Booking Not Available
        </p>
      </div>

      <a v-affiliate-link
        v-if="isAffiliateLink"
        class="cs-btn-book-primary sticky-cta"
        :href="stickyPartnerUrl"
        target="_blank"
        rel="noopener noreferrer sponsored"
        :aria-label="`${stickyCta} on ${partnerName} (opens booking partner in a new tab)`"
        aria-describedby="sticky-affiliate-desc"
        :tabindex="stickyVisible ? null : -1"
        @click="handleBookClick"
      >
        {{ stickyCta }} ↗
      </a>
      <a
        v-else-if="alertTarget"
        class="cs-btn-book-primary sticky-cta"
        :href="`#${alertTarget}`"
        :tabindex="stickyVisible ? null : -1"
        @click.prevent="goToAlert"
      >
        <i class="fas fa-bell" aria-hidden="true"></i>
        Price Alert
      </a>
    </div>
  </div>
</template>

<script>
import { notifyAffiliateClick } from '@/utils/affiliateBus';
import { bookingDisclosureCopy } from '@/utils/bookingDisclosure';
import {
  tierCopy,
  stickyCtaLabel,
  STICKY_DISCLOSURE_TEXT,
  STICKY_DISCLOSURE_LINK_TEXT,
  FULL_DISCLOSURE_ID
} from '@/utils/bookingTierCopy';

import { formatPriceAmount } from '@/utils/formatMoney';
export default {
  name: 'BookNowButton',
  props: {
    cruiseId: {
      type: [Number, String],
      required: true
    },
    price: {
      type: [Number, String],
      default: null
    },
    provider: {
      type: String,
      default: 'Cruise Partner'
    },
    // CP-17: affiliate wiring — disabled by default until Bryan activates CJ.
    affiliateEnabled: {
      type: Boolean,
      default: false
    },
    partnerUrl: {
      type: String,
      default: null
    },
    partnerName: {
      type: String,
      default: 'CruiseDirect'
    },
    cabinType: {
      type: String,
      default: ''
    },
    // CP-55: "Updated … UTC" for the lead-in collection. No label means the
    // age cannot be shown as current, so the fare is withheld.
    updatedLabel: {
      type: String,
      default: ''
    },
    // CP-76: "Last observed … UTC" when the fare is stale. This is not a
    // freshness cue and must not unlock the dollar.
    ageNote: {
      type: String,
      default: ''
    },
    // CP-100: "Price not current" when a fare exists but is not fresh.
    // Empty stays "Price unavailable" (no fare on the payload).
    hiddenReason: {
      type: String,
      default: ''
    },
    // CP-120: id of the on-page Price Alert card. While Book is off, the
    // primary CTA slot jumps there instead of showing not-bookable chrome.
    alertTarget: {
      type: String,
      default: ''
    },
    // CP-143: CP-141b landing tier + the values for the "last collected" sub-line.
    landingTier: {
      type: String,
      default: ''
    },
    lineName: {
      type: String,
      default: ''
    },
    collectedAt: {
      type: String,
      default: ''
    }
  },
  computed: {
    // CP-144: the sticky phone bar credits its own surface (detail_sticky).
    stickyPartnerUrl() {
      return this.partnerUrl ? this.partnerUrl.replace(/([?&]source=)detail(?=&|$)/, '$1detail_sticky') : this.partnerUrl;
    },
    tierCopy() {
      return tierCopy({ tier: this.landingTier, line: this.lineName, collectedAt: this.collectedAt || null });
    },
    // CP-213: show the bar once the price card is out of view, and not while the
    // Price Alert form is on screen. No IntersectionObserver: always available.
    stickyVisible() {
      if (!this.hasObserver) return true;
      return !this.priceCardInView && !this.alertInView;
    },
    stickyCta() {
      return stickyCtaLabel(this.landingTier);
    },
    // CP-213: one-line disclosure (constants live in bookingTierCopy.js).
    stickyDisclosureText() {
      return STICKY_DISCLOSURE_TEXT;
    },
    stickyDisclosureLinkText() {
      return STICKY_DISCLOSURE_LINK_TEXT;
    },
    fullDisclosureId() {
      return FULL_DISCLOSURE_ID;
    },
    unavailableCopy() {
      const text = (this.hiddenReason || '').trim();
      return text || 'Price unavailable';
    },
    hasPrice() {
      if (!this.updatedLabel) return false;
      if (this.price === null || this.price === undefined || this.price === '' || this.price === 'N/A') {
        return false;
      }
      const n = Number(this.price);
      return !Number.isNaN(n) && n > 0;
    },
    displayPrice() {
      return formatPriceAmount(this.price);
    },
    cabinLabel() {
      const t = (this.cabinType || '').trim();
      return t && t !== 'Unknown' ? t : '';
    },
    disclosure() {
      return bookingDisclosureCopy({
        affiliateEnabled: this.affiliateEnabled,
        partnerUrl: this.partnerUrl,
        partnerName: this.partnerName,
      });
    },
    isAffiliateLink() {
      return this.disclosure.bookable;
    }
  },
  data() {
    // CP-213: hidden until the price card scrolls out of view. Without
    // IntersectionObserver the bar simply stays available.
    return { priceCardInView: true, alertInView: false, hasObserver: false, observer: null };
  },
  mounted() {
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') return;
    this.hasObserver = true;
    this.observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.$refs.priceCard) this.priceCardInView = entry.isIntersecting;
        else this.alertInView = entry.isIntersecting;
      });
    });
    if (this.$refs.priceCard) this.observer.observe(this.$refs.priceCard);
    const alertEl = this.alertTarget ? document.getElementById(this.alertTarget) : null;
    if (alertEl) this.observer.observe(alertEl);
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
  },
  methods: {
    goToDisclosure() {
      if (typeof document === 'undefined') return;
      const target = document.getElementById(FULL_DISCLOSURE_ID);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    },
    goToAlert() {
      if (typeof document === 'undefined' || !this.alertTarget) return;
      const target = document.getElementById(this.alertTarget);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const field = target.querySelector('input[type="email"]');
      if (field) field.focus({ preventScroll: true });
    },
    handleBookClick() {
      // Parent may hook analytics / fallback behaviour via @book-click.
      this.$emit('book-click', {
        cruiseId: this.cruiseId,
        partnerName: this.partnerName,
        partnerUrl: this.partnerUrl,
        cabinType: this.cabinType
      });
      // Toast is global via the event bus (AffiliateToast in App.vue).
      notifyAffiliateClick({ partnerName: this.partnerName });
    }
  }
};
</script>

<style scoped>
.book-now-container {
  background-color: var(--cs-signal-mist);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  text-align: center;
  box-shadow: var(--shadow-card);
  border: 1px solid var(--cs-card-border);
}

.price-display {
  display: flex;
  flex-direction: column;
  margin-bottom: var(--space-4);
}

.price-label {
  font-size: var(--text-small);
  color: var(--color-text-muted);
}

.price-value {
  font-size: 32px;
  font-weight: bold;
  color: var(--cs-price);
  margin: var(--space-2) 0;
}

.price-cabin {
  font-size: var(--text-small);
  font-weight: 500;
  color: var(--color-text-muted);
}

.price-person {
  font-size: var(--text-small);
  color: var(--color-text-muted);
}

.price-freshness {
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  line-height: 1.35;
  color: var(--color-text-muted);
}

.price-unavailable {
  font-size: var(--text-body);
  color: var(--color-text-muted);
  font-style: italic;
  padding: var(--space-3) 0;
}

.book-now-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  background-color: var(--cs-fill);
  color: white;
  font-size: var(--text-body);
  font-weight: 700;
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-pill);
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: background var(--transition-normal), transform var(--transition-fast);
  min-height: var(--touch-min);
  width: 100%;
  box-sizing: border-box;
}

.book-now-inline {
  margin-bottom: var(--space-2);
}

.book-now-link {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: var(--space-4);
}

.book-now-link i {
  font-size: 18px;
}

/* CP-120: affiliate OFF — Price Alert CTA sits tight under the price */
.book-now-alert-cta {
  margin-bottom: var(--space-3);
}

/* CP-97: Affiliate-OFF — quiet text/link weight instead of outlined pill */
.book-now-quiet {
  margin: 0 0 var(--space-4);
  padding: 0;
  border: none;
  background: transparent;
  color: var(--cs-muted);
  font-size: var(--text-small);
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  cursor: default;
}

.book-now-button:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
}

.book-now-button i {
  font-size: 18px;
}

.booking-info {
  font-size: var(--text-caption);
  color: var(--color-text-muted);
}

.booking-info p {
  margin: var(--space-2) 0;
}

.disclaimer {
  font-style: italic;
  font-size: 11px;
}

/* CP-213: slim 56px phone bar. Fixed, so showing or hiding it never moves
   page content (no layout shift). The page reserves the room in
   .cruise-detail padding-bottom. */
.sticky-book-bar {
  display: block;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  box-sizing: border-box;
  background: var(--cs-card);
  border-top: 1px solid var(--cs-card-border);
  box-shadow: 0 -2px 10px rgba(8, 63, 69, 0.14);
  padding: 0 8px env(safe-area-inset-bottom, 0px);
  transform: translateY(0);
  visibility: visible;
  transition: transform 160ms ease-out, visibility 0s linear 0s;
}

.sticky-book-bar--hidden {
  transform: translateY(100%);
  visibility: hidden;
  box-shadow: none;
  transition: transform 160ms ease-in, visibility 0s linear 160ms;
}

@media (prefers-reduced-motion: reduce) {
  .sticky-book-bar,
  .sticky-book-bar--hidden {
    transition: none;
  }
}

.sticky-book-inner {
  max-width: 1200px;
  height: 55px; /* + 1px top border = 56px */
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sticky-price {
  flex: 0 0 auto;
  max-width: 100%;
  min-width: 0;
  text-align: left;
}

.sticky-price-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
  min-width: 0;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sticky-from,
.sticky-per-person {
  font-size: 12px;
  color: var(--color-text-muted);
}

.sticky-amount {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--cs-price);
}

.sticky-cabin {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-muted);
}

.sticky-unavailable {
  font-size: var(--text-small);
  color: var(--color-text-muted);
  font-style: italic;
}

/* One line, >= 12px, >= 4.5:1 on the white bar (ink #1A2330 / teal #0B5B63). */
.sticky-disclosure {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--cs-ink);
  white-space: nowrap;
}

/* The link box is a real 44px tap target; negative margins keep the text
   line at 16px so the bar stays 56px. */
.sticky-disclosure-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 44px;
  margin: -14px -6px -14px -2px;
  padding: 0 6px;
  vertical-align: middle;
  font-size: 12px;
  font-weight: 600;
  color: var(--cs-signal-teal);
  text-decoration: underline;
}

/* CP-120: quiet status reads as a caption under the sticky price */
.sticky-quiet {
  margin: 0;
  text-align: left;
  font-size: 12px;
  line-height: 16px;
  color: var(--cs-ink);
}

/* The CTA takes the width the one-line disclosure leaves over and wraps its
   label (2 to 3 lines) inside the 44px+ button. The bar stays 56px. */
.sticky-cta {
  flex: 1 1 0;
  min-width: 72px;
  width: auto;
  min-height: 44px;
  margin-bottom: 0;
  padding: 3px var(--space-2);
  font-size: 12px;
  line-height: 1.15;
  white-space: normal;
  text-align: center;
}

/* Below 359px the one-line disclosure plus the button cannot share 56px;
   let the line wrap and the bar grow rather than clip the disclosure. */
@media (max-width: 358px) {
  .sticky-book-inner {
    height: auto;
    min-height: 55px;
    padding: 4px 0;
  }

  .sticky-price {
    flex: 1 1 auto;
  }

  .sticky-disclosure {
    white-space: normal;
  }
}

/* CP-213: compact price card on phones (From / price left, per person /
   updated right) so the CTA and the Itinerary start on the first screen.
   Same copy and same disclosure as before; only spacing and arrangement. */
@media (max-width: 767px) {
  .book-now-container {
    padding: var(--space-3);
    text-align: left;
  }

  .price-display {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "label person"
      "value fresh";
    align-items: end;
    column-gap: var(--space-3);
    margin-bottom: var(--space-2);
  }

  .price-label { grid-area: label; }
  .price-person { grid-area: person; text-align: right; }
  .price-value { grid-area: value; margin: 0; font-size: 28px; line-height: 1.15; white-space: nowrap; }
  .price-display .price-freshness { grid-area: fresh; margin-top: 0; text-align: right; }
  .price-display .price-unavailable { grid-column: 1 / -1; padding: var(--space-2) 0; }

  .book-now-link {
    margin-bottom: var(--space-2);
    padding: var(--space-3);
    font-size: 15px;
    line-height: 1.2;
  }

  .booking-info p {
    margin: var(--space-1) 0;
  }

  .booking-info .cs-affiliate-disclosure {
    padding: 8px 10px;
  }

  /* 44px tap target for the existing "Learn more" without growing the line */
  .booking-info .cs-affiliate-disclosure a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    margin: -14px 0;
    padding: 0 6px;
  }
}

@media (min-width: 768px) {
  .sticky-book-bar {
    display: none;
  }

  .book-now-button {
    font-size: var(--text-h3);
  }

  .price-value {
    font-size: 32px;
  }
}
</style>